// Single source of truth for "is a text-formatting interaction in progress".
//
// While held, the editor preview freezes its rendered model so applying formatting
// (e.g. dragging the colour picker) doesn't rebuild the contenteditable and lose the
// selection. It is held ONLY for formatting — never for block operations like
// duplicate/move/insert — so those still re-render immediately.
//
// Lives in the editor package so MjmlDomRender (editor) and the toolbar (extensions)
// share one flag.
let isApplyingFormat = false;
let interactionHoldCount = 0;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;
let watchdogTimer: ReturnType<typeof setTimeout> | null = null;

// Safety net: the guard must NEVER stay held indefinitely. If a hold is somehow
// never released, a stuck flag would freeze the canvas and suppress model sync.
const MAX_HOLD_MS = 8000;

const releaseListeners = new Set<() => void>();

// Lets the preview re-run the model sync it skipped while the guard was held.
export const subscribeFormatRelease = (listener: () => void) => {
	releaseListeners.add(listener);
	return () => {
		releaseListeners.delete(listener);
	};
};

const notifyRelease = () => releaseListeners.forEach(listener => listener());

const clearReleaseTimer = () => {
	if (releaseTimer !== null) {
		clearTimeout(releaseTimer);
		releaseTimer = null;
	}
};

const clearWatchdog = () => {
	if (watchdogTimer !== null) {
		clearTimeout(watchdogTimer);
		watchdogTimer = null;
	}
};

const forceRelease = () => {
	interactionHoldCount = 0;
	isApplyingFormat = false;
	clearReleaseTimer();
	clearWatchdog();
	notifyRelease();
};

const scheduleWatchdog = () => {
	clearWatchdog();
	watchdogTimer = setTimeout(forceRelease, MAX_HOLD_MS);
};

const scheduleRelease = (delayMs: number) => {
	clearReleaseTimer();
	releaseTimer = setTimeout(() => {
		releaseTimer = null;
		if (interactionHoldCount === 0) {
			isApplyingFormat = false;
			notifyRelease();
		}
	}, delayMs);
};

export const getIsApplyingFormat = () => isApplyingFormat;

export const beginFormatInteraction = () => {
	interactionHoldCount += 1;
	isApplyingFormat = true;
	clearReleaseTimer();
	scheduleWatchdog();
};

export const endFormatInteraction = () => {
	interactionHoldCount = Math.max(0, interactionHoldCount - 1);
	if (interactionHoldCount === 0) {
		isApplyingFormat = false;
		clearReleaseTimer();
		clearWatchdog();
		notifyRelease();
	}
};

export const pulseFormatApplication = (releaseDelayMs = 120) => {
	isApplyingFormat = true;
	if (interactionHoldCount > 0) {
		clearReleaseTimer();
		return;
	}
	scheduleRelease(releaseDelayMs);
};
